import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from '../../util/reactIntl';

import Modal from '../Modal/Modal';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { createShippoAddress } from '../../ducks/shippo.duck';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { OWN } from '../../util/constants';
import {
  checkIfEnabledNonUSOrders,
  getOtherShippingPreferences,
  getOwnShippingPreference,
  getShippingDetails,
  getThresholdParams,
  hasValidShippingDetails,
} from '../../util/userDataExtractor';
import ShippingDetailsForm from '../../containers/ShippingDetailsPage/ShippingDetailsForm/ShippingDetailsForm';
import {
  buildShippingDetailsInitialValues,
  createShippingDetailsSubmitHandler,
} from '../../containers/ShippingDetailsPage/shippingDetails.helpers';

import css from './ShippingPreferenceModal.module.css';

export const shouldShowShippingPreferenceModal = (shippingPreferenceOrUser, isOpen) => {
  if (!isOpen) {
    return false;
  }

  if (shippingPreferenceOrUser && typeof shippingPreferenceOrUser === 'object') {
    return !hasValidShippingDetails(shippingPreferenceOrUser);
  }

  return !shippingPreferenceOrUser;
};

export const didSaveShippingDetailsSucceed = ({ values, result }) => {
  if (values?.shippingPreference === OWN) {
    return (
      result?.meta?.requestStatus === 'fulfilled' ||
      (typeof result?.type === 'string' && !result.type.endsWith('/rejected'))
    );
  }

  return result?.statusCode === 201;
};

const ShippingPreferenceModal = ({
  isOpen,
  onClose = () => {},
  onSuccess = () => {},
  context = 'general',
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const currentUser = useSelector(state => state.user.currentUser);
  const profileState = useSelector(state => state.ProfileSettingsPage);
  const saveShippoAddressInProgress = useSelector(
    state => state.shippo?.saveShippoAddressInProgress || false
  );
  const saveShippoAddressError = useSelector(state => state.shippo?.saveShippoAddressError);
  const shippoAddressSaved = useSelector(state => state.shippo?.shippoAddressSaved || false);

  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = typeof isOpen === 'boolean';

  const shippingPreference = getOwnShippingPreference(currentUser);
  const shippingAddress = getShippingDetails(currentUser);
  const otherShippingPreferences = getOtherShippingPreferences(currentUser);
  const thresholdParams = getThresholdParams(currentUser);
  const enableNonUSOrders = checkIfEnabledNonUSOrders(currentUser);

  useEffect(() => {
    if (!isControlled) {
      setInternalIsOpen(!hasValidShippingDetails(currentUser));
    }
  }, [currentUser, isControlled]);

  const requestedOpen = isControlled ? isOpen : internalIsOpen;
  const shouldRender = isControlled
    ? Boolean(requestedOpen)
    : shouldShowShippingPreferenceModal(currentUser, requestedOpen);

  const closeModal = () => {
    if (!isControlled) {
      setInternalIsOpen(false);
    }
    onClose();
  };

  const initialValues = useMemo(() => {
    return buildShippingDetailsInitialValues({
      shippingAddress,
      shippingPreference,
      shippingType: otherShippingPreferences?.shippingType,
      thresholdParams,
      otherShippingPreferences,
      enableNonUSOrders,
    });
  }, [
    enableNonUSOrders,
    otherShippingPreferences,
    shippingAddress,
    shippingPreference,
    thresholdParams,
  ]);

  if (!shouldRender) {
    return null;
  }

  const submitShippingDetailsFromProfile = createShippingDetailsSubmitHandler({
    onUpdateProfile: params => dispatch(updateProfile(params)),
    onCreateShippoAddress: params => dispatch(createShippoAddress(params)),
  });

  const handleSubmit = async values => {
    const result = await submitShippingDetailsFromProfile(values);

    if (didSaveShippingDetailsSucceed({ values, result })) {
      onSuccess({ context, values });
      closeModal();
    }
  };

  return (
    <Modal
      id="ShippingPreferenceModal"
      isOpen={requestedOpen}
      onClose={closeModal}
      onManageDisableScrolling={(componentId, disableScrolling) =>
        dispatch(manageDisableScrolling(componentId, disableScrolling))
      }
      hideCloseButton
      usePortal
      modalTitle={intl.formatMessage({ id: 'ShippingPreferenceModal.modalTitle' })}
    >
      <div className={css.payoutContainer}>
        <ShippingDetailsForm
          className={css.form}
          initialValues={initialValues}
          currentUser={currentUser}
          onSubmit={handleSubmit}
          inProgress={saveShippoAddressInProgress || profileState?.updateInProgress}
          ready={shippoAddressSaved}
          updateProfileError={profileState?.updateProfileError || saveShippoAddressError}
        />
      </div>
    </Modal>
  );
};

ShippingPreferenceModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  context: PropTypes.string,
};

export default ShippingPreferenceModal;
